"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UndefinedValueMergeBehavior = void 0;
exports.getPropertyByPath = getPropertyByPath;
exports.merge = merge;
exports.mergeLeft = mergeLeft;
exports.deepClone = deepClone;
exports.createObjectByPath = createObjectByPath;
exports.checkObjectHasNestedKey = checkObjectHasNestedKey;
exports.cleanNullValues = cleanNullValues;
exports.enumFromValue = enumFromValue;
exports.asNumber = asNumber;
exports.asBoolean = asBoolean;
exports.omit = omit;
exports.cleanTypenameField = cleanTypenameField;
exports.isObjectEmpty = isObjectEmpty;
exports.getDiff = getDiff;
exports.filterNullInClauseVariables = filterNullInClauseVariables;
exports.isLeftEqual = isLeftEqual;
exports.hasAllPropertiesDefined = hasAllPropertiesDefined;
exports.removeNullishValues = removeNullishValues;
const clone_deep_1 = __importDefault(require("clone-deep"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const ArrayHelper_1 = require("./ArrayHelper");
const NullOrUndefinedHelper_1 = __importDefault(require("./NullOrUndefinedHelper"));
/**
 * Behavior for undefined properties when merging
 */
var UndefinedValueMergeBehavior;
(function (UndefinedValueMergeBehavior) {
    // Remove undefined from result.
    UndefinedValueMergeBehavior[UndefinedValueMergeBehavior["IGNORE_AFTER_MERGE"] = 0] = "IGNORE_AFTER_MERGE";
    // Remove undefined from source.
    UndefinedValueMergeBehavior[UndefinedValueMergeBehavior["IGNORE_BEFORE_MERGE"] = 1] = "IGNORE_BEFORE_MERGE";
})(UndefinedValueMergeBehavior || (exports.UndefinedValueMergeBehavior = UndefinedValueMergeBehavior = {}));
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Gets the value at path of object.
 *
 * See: https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_get
 *
 * @param object - the object
 * @param path - the path to property
 * @param defaultValue - the default value to use if the path does not exist.
 */
function getPropertyByPath(object, path, defaultValue) {
    const travel = (regexp) => String.prototype.split
        .call(path, regexp)
        .filter(Boolean)
        // eslint-disable-next-line unicorn/no-array-reduce
        .reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (response, key) => response !== null && response !== undefined ? response[key] : response, object);
    const result = travel(/[,[\]]+?/) || travel(/[,.[\]]+?/);
    return result === undefined || result === object
        ? defaultValue
        : result;
}
/**
 * Performs a deep merge of source objectA with target objectB by default.
 * objectB value overrides objectA value for the same key.
 * Returns new object with the properties of objectA and objectB merged
 * @param objectA target object.
 * @param objectB source object.
 * @param mergeOptions options to merge two object
 */
function merge(objectA, objectB, mergeOptions = { mergeNested: true }) {
    const target = objectA;
    const source = objectB;
    const returnValue = Array.isArray(target)
        ? [...target]
        : Object.assign({}, target);
    const { undefinedMergeBehavior, mergeNested } = mergeOptions !== null && mergeOptions !== void 0 ? mergeOptions : {};
    Object.entries(objectB).forEach(([key, value]) => {
        if (value instanceof Object && returnValue[key]) {
            returnValue[key] = mergeNested ? merge(returnValue[key], source[key], mergeOptions) : value;
        }
        else {
            const mergeValue = source[key];
            if (undefinedMergeBehavior === UndefinedValueMergeBehavior.IGNORE_AFTER_MERGE &&
                mergeValue === undefined) {
                delete returnValue[key];
            }
            else if (undefinedMergeBehavior === UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE &&
                mergeValue === undefined) {
                returnValue[key] = target[key];
            }
            else {
                returnValue[key] = source[key];
            }
        }
    });
    return returnValue;
}
/**
 * Merges objectA and objectB and returns object of type ObjectTypeA. Following rules are followed when merging:
 * 1. If property A is in objectA and not in objectB then the value of objectA is retained iff objectB has the property key defined.
 * 2. If property A is in objectA and in objectB the property is merged such that objectB value is retained.
 * 3. If property A is in objectB and not in objectA then the value is not merged and ignored.
 * 4. If property A in objectA is undefined and is defined in objectB, then the value of objectB will be merged and retained.
 * @param objectA the object to merge to.
 * @param objectB the object to merge from.
 * @param includeNested whether to merge nested object as well or not.
 * @param includeUndefined whether to include undefined value or not. If set to true then no undefined values are returned.
 */
function mergeLeft(objectA, objectB, includeNested, includeUndefined) {
    const returnType = {};
    const objectASource = objectA;
    const objectBSource = objectB;
    Object.entries(objectA).forEach(([key, value]) => {
        returnType[key] = objectASource[key];
        const valueInB = objectBSource[key];
        if (value instanceof Object &&
            valueInB !== undefined &&
            includeNested &&
            valueInB instanceof Object) {
            returnType[key] = mergeLeft(value, valueInB, includeNested);
        }
        else if (Object.prototype.hasOwnProperty.call(objectBSource, key) || valueInB !== undefined) {
            returnType[key] = valueInB;
        }
        if (!includeUndefined && returnType[key] === undefined) {
            delete returnType[key];
        }
    });
    return returnType;
}
/**
 * Creates object by given path within the specified object and assigns the value to the created path key.
 * @param object the object to modify.
 * @param path the path to create the sub object.
 * @param value the value to assign to the path.
 */
function createObjectByPath(object, path, value) {
    let current = object;
    let pathParts = typeof path === 'string' ? path.split('.') : path;
    while (pathParts.length > 1) {
        const [head, ...tail] = pathParts;
        pathParts = tail;
        if (current[head] === undefined) {
            current[head] = {};
        }
        current = current[head];
    }
    current[pathParts[0]] = value;
}
/**
 * Checks whether the specified object has the keys defined for the given path. Returns true if key exists else false.
 * @param object the object to check.
 * @param path the key path.
 */
function checkObjectHasNestedKey(object, path) {
    let current = object;
    let pathParts = typeof path === 'string' ? path.split('.') : path;
    while (pathParts.length > 0) {
        const [head, ...tail] = pathParts;
        pathParts = tail;
        if (current === undefined || !Object.prototype.hasOwnProperty.call(current, head)) {
            return false;
        }
        current = current[head];
    }
    return true;
}
/**
 * Perform a deep clone of an object.  Abstracted so we can swap implementations as necessary.
 * @param object The object to clone
 * @return a deep clone of the object
 */
function deepClone(object) {
    return object ? (0, clone_deep_1.default)(object) : object;
}
/**
 * Removes keys with null values
 * @param object the object to remove the keys with null value
 */
function cleanNullValues(object) {
    if (!object) {
        return {};
    }
    const clone = deepClone(object);
    Object.keys(clone).forEach(key => {
        const typedKey = key;
        const value = clone[typedKey];
        if (value === null) {
            delete clone[typedKey];
        }
        else if (typeof value === 'object') {
            if (Array.isArray(value)) {
                value.forEach((arrayValue, idx) => {
                    if (typeof arrayValue === 'object') {
                        value[idx] = cleanNullValues(arrayValue);
                    }
                });
            }
            else {
                clone[typedKey] = cleanNullValues(value);
            }
        }
    });
    return clone;
}
/**
 * Look up enum by its string value
 *
 * @param enumObject - the enum object
 * @param value - the value
 */
function enumFromValue(enumObject, value) {
    return Object.values(enumObject).find(enumObjectValue => enumObjectValue === value);
}
/**
 * If a value exists, return it as a number, otherwise return a default.
 */
function asNumber(numericValue, defaultValue) {
    if (numericValue) {
        const parsedNumber = Number(numericValue);
        return Number.isNaN(parsedNumber) ? defaultValue : parsedNumber;
    }
    return defaultValue;
}
/**
 * If a value exists, return it as a boolean, otherwise return a default.
 */
function asBoolean(booleanValue, defaultValue) {
    if (booleanValue) {
        return `${booleanValue}`.toLowerCase() === 'true';
    }
    return defaultValue !== null && defaultValue !== void 0 ? defaultValue : false;
}
/**
 * Returns a new object with the specified property omitted.
 * @param o The object
 * @param k The property name to omit
 */
function omit(o, k) {
    return Object.assign(Object.assign({}, o), { [k]: undefined });
}
/**
 * Removes __typenames keys
 * @param object the object to remove the __typenames keys
 */
function cleanTypenameField(object) {
    const clone = deepClone(object);
    Object.keys(clone).forEach(key => {
        const value = clone[key];
        if (key === '__typename') {
            delete clone[key];
        }
        else if (typeof value === 'object') {
            if (Array.isArray(value)) {
                value.forEach((arrayValue, idx) => {
                    if (typeof arrayValue === 'object') {
                        value[idx] = cleanTypenameField(arrayValue);
                    }
                });
            }
            else if (value !== null) {
                clone[key] = cleanTypenameField(value);
            }
        }
    });
    return clone;
}
function isObjectEmpty(object) {
    if ((0, NullOrUndefinedHelper_1.default)(object)) {
        return true;
    }
    return Object.keys(object).length === 0;
}
function getDiff(referencedObject, objectA, objectB) {
    const target = deepClone(objectA);
    const source = deepClone(objectB);
    const returnValue = Array.isArray(target)
        ? [...target]
        : Object.assign({}, target);
    Object.entries(objectB).forEach(([key, value]) => {
        if (value instanceof Object && returnValue[key]) {
            const diffValue = getDiff(referencedObject ? referencedObject[key] : {}, objectA[key], objectB[key]);
            if (Object.keys(diffValue).length > 0) {
                returnValue[key] = diffValue;
            }
            else {
                delete returnValue[key];
            }
        }
        else {
            if (referencedObject && referencedObject[key] ? true : target[key] !== source[key]) {
                returnValue[key] = source[key];
            }
            else {
                delete returnValue[key];
            }
        }
    });
    return returnValue;
}
function filterNullInClauseVariables(object) {
    for (const key in object) {
        if (key === 'in' || key === 'nin') {
            object[key] = (0, ArrayHelper_1.getNonNullArray)(object[key]);
        }
        else if (typeof object[key] === 'object') {
            if (Array.isArray(object[key])) {
                for (let i = 0; i < object[key].length; i++) {
                    filterNullInClauseVariables(object[key][i]);
                }
            }
            else {
                filterNullInClauseVariables(object[key]);
            }
        }
    }
}
/**
 * Returns true if values of all key in left object recursively matches with the values in the right.
 * The right object may or may not be equal to the left in entirety.
 * @param left the source to compare to
 * @param right the target to compare to
 */
function isLeftEqual(left, right) {
    if (left === null) {
        return right === null;
    }
    if (left === undefined) {
        return right === undefined;
    }
    if (right === null || right === undefined) {
        return false;
    }
    if (left === right) {
        return true;
    }
    return Object.keys(left).every(key => {
        if (typeof left[key] === 'object') {
            return isLeftEqual(left[key], right[key]);
        }
        return (0, react_fast_compare_1.default)(left[key], right[key]);
    });
}
function hasAllPropertiesDefined(object) {
    for (const key in object) {
        if (object.hasOwnProperty(key)) {
            const value = object[key];
            if (value === undefined || value === null || value === '') {
                return false;
            }
        }
    }
    return true;
}
/**
 * Removes nullish values from an object
 *
 * @param object the object to remove nullish values from
 */
function removeNullishValues(object) {
    return Object.fromEntries(Object.entries(object).filter(([, value]) => value != null));
}
//# sourceMappingURL=ObjectHelper.js.map